import React, { useState } from "react";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { TextField } from "../ui/components/TextField";
import { Select } from "../ui/components/Select";
import { FeatherX } from "@subframe/core";
import { FeatherCalendar } from "@subframe/core";
import { journalsAPI, MakeRecurringRequest } from "../services/api";
import { ACTION_CODES, ActionCode } from "../shared/constants/permissions";

interface MakeRecurringModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (success: boolean, message?: string) => void;
  journalId: string;
  needsReviewForCreateRecurring?: boolean;
  hasApprovedReview?: (actionCode: ActionCode) => boolean;
  onReviewNeeded?: (requestData: MakeRecurringRequest) => void;
}

export interface RecurringData {
  recurringJournalName: string;
  repeatFrequency: string;
  recurrenceInterval?: number;
  referenceNumberPrefix: string;
  startsOn: string;
  endsOn: string;
  neverExpires: boolean;
}

const MakeRecurringModal: React.FC<MakeRecurringModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  journalId,
  needsReviewForCreateRecurring = false,
  hasApprovedReview,
  onReviewNeeded,
}) => {
  // Form state
  const [recurringJournalName, setRecurringJournalName] = useState("");
  const [repeatFrequency, setRepeatFrequency] = useState("");
  const [recurrenceInterval, setRecurrenceInterval] = useState<number | "">("");
  const [referenceNumberPrefix, setReferenceNumberPrefix] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [neverExpires, setNeverExpires] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  // Helper function to check if a date is the first day of a month
  const isFirstDayOfMonth = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    return date.getDate() === 1;
  };

  // Helper function to check if a date is the last day of a month
  const isLastDayOfMonth = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    return nextDay.getDate() === 1;
  };

  // Helper function to check if a date is end of quarter
  const isEndOfQuarter = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    const month = date.getMonth();
    const day = date.getDate();

    return (
      (month === 2 && day === 31) || // March 31
      (month === 5 && day === 30) || // June 30
      (month === 8 && day === 30) || // September 30
      (month === 11 && day === 31) // December 31
    );
  };

  // Helper function to check if a date is end of year
  const isEndOfYear = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    return date.getMonth() === 11 && date.getDate() === 31;
  };

  // Helper function to get minimum date based on frequency
  const getMinStartDate = (frequency: string): string => {
    const todayDate = new Date();

    switch (frequency) {
      case "every_n_days":
        if (recurrenceInterval) {
          const minDate = new Date(todayDate);
          minDate.setDate(minDate.getDate() + Number(recurrenceInterval));
          return minDate.toISOString().split("T")[0];
        }
        return todayDate.toISOString().split("T")[0];

      default:
        // For all other frequencies, tomorrow is the minimum
        const tomorrow = new Date(todayDate);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split("T")[0];
    }
  };

  // Helper function to get help text for start date based on frequency
  const getStartDateHelpText = (frequency: string): string => {
    switch (frequency) {
      case "start_of_month":
        return "⚠️ You must select the 1st day of a month";
      case "end_of_month":
        return "⚠️ You must select the last day of a month";
      case "end_of_quarter":
        return "⚠️ You must select a quarter-end date: Mar 31, Jun 30, Sep 30, or Dec 31";
      case "end_of_year":
        return "⚠️ You must select December 31st";
      case "date_of_month":
        return "⚠️ You must select any date EXCEPT the 1st or last day of the month";
      case "every_n_days":
        return recurrenceInterval
          ? `⚠️ Start date must be at least ${recurrenceInterval} days from today`
          : "Select number of days first";
      default:
        return "";
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!recurringJournalName.trim() || !repeatFrequency || !startsOn) {
      onSubmit(false, "Please fill in all required fields");
      return;
    }

    if (repeatFrequency === "every_n_days" && !recurrenceInterval) {
      onSubmit(false, "Please enter the number of days for recurrence");
      return;
    }

    if (!neverExpires && !endsOn) {
      onSubmit(false, "Please select an end date or check 'Never expires'");
      return;
    }

    // Validate start date based on frequency
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const selectedDate = new Date(startsOn);
    selectedDate.setHours(0, 0, 0, 0);

    if (repeatFrequency === "start_of_month" && !isFirstDayOfMonth(startsOn)) {
      onSubmit(false, "Start date must be the 1st day of a month");
      return;
    }

    if (repeatFrequency === "end_of_month" && !isLastDayOfMonth(startsOn)) {
      onSubmit(false, "Start date must be the last day of a month");
      return;
    }

    if (repeatFrequency === "end_of_quarter" && !isEndOfQuarter(startsOn)) {
      onSubmit(
        false,
        "Start date must be a quarter-end date (Mar 31, Jun 30, Sep 30, or Dec 31)"
      );
      return;
    }

    if (repeatFrequency === "end_of_year" && !isEndOfYear(startsOn)) {
      onSubmit(false, "Start date must be December 31st");
      return;
    }

    if (
      repeatFrequency === "date_of_month" &&
      (isFirstDayOfMonth(startsOn) || isLastDayOfMonth(startsOn))
    ) {
      onSubmit(false, "Start date cannot be the 1st or last day of the month");
      return;
    }

    // Prepare request data
    const requestData: MakeRecurringRequest = {
      template_name: recurringJournalName,
      recurrence_frequency: repeatFrequency,
      recurrence_interval:
        repeatFrequency === "every_n_days"
          ? (recurrenceInterval as number)
          : repeatFrequency === "date_of_month"
          ? new Date(startsOn).getDate()
          : null,
      reference_number_prefix: referenceNumberPrefix.trim() || undefined,
      starts_on: startsOn,
      ends_on: neverExpires ? null : endsOn,
      never_expires: neverExpires,
    };

    // Check if review is required
    if (
      needsReviewForCreateRecurring &&
      hasApprovedReview &&
      !hasApprovedReview(ACTION_CODES.CREATE_RECURRING) &&
      onReviewNeeded
    ) {
      // Call the callback to show review modal
      onReviewNeeded(requestData);
      return;
    }

    // No review needed, proceed with submission
    try {
      setLoading(true);

      const response = await journalsAPI.makeJournalRecurring(
        journalId,
        requestData
      );

      onSubmit(
        true,
        response.message || "Journal converted to recurring successfully"
      );
      handleClose();
    } catch (error) {
      console.error("Error making journal recurring:", error);
      onSubmit(
        false,
        error instanceof Error
          ? error.message
          : "Failed to make journal recurring"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    // Reset form
    setRecurringJournalName("");
    setRepeatFrequency("");
    setRecurrenceInterval("");
    setReferenceNumberPrefix("");
    setStartsOn("");
    setEndsOn("");
    setNeverExpires(false);
    setLoading(false);
    onOpenChange(false);
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-192 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Make Recurring
          </span>
          <IconButton icon={<FeatherX />} onClick={handleClose} />
        </div>
        <div className="flex w-full items-start gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
            <TextField
              className="h-auto w-full flex-none"
              label="Recurring Journal Name"
              helpText=""
            >
              <TextField.Input
                placeholder="Enter recurring journal name"
                value={recurringJournalName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setRecurringJournalName(event.target.value)
                }
              />
            </TextField>
            <TextField
              className="h-auto w-full flex-none"
              label="Starts On"
              helpText={getStartDateHelpText(repeatFrequency)}
              icon={<FeatherCalendar />}
            >
              <TextField.Input
                type="date"
                placeholder="Select start date"
                value={startsOn}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setStartsOn(event.target.value)
                }
                min={getMinStartDate(repeatFrequency)}
                disabled={
                  repeatFrequency === "every_n_days" && !recurrenceInterval
                }
              />
            </TextField>
            <TextField
              className="h-auto w-full flex-none"
              label="Reference Prefix"
              helpText="Optional prefix for generated journal entries (e.g., 'RENT')"
            >
              <TextField.Input
                placeholder="Enter prefix (optional)"
                value={referenceNumberPrefix}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setReferenceNumberPrefix(event.target.value)
                }
              />
            </TextField>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 flex-none rounded-full border border-solid border-neutral-400"
                checked={neverExpires}
                onChange={(e) => setNeverExpires(e.target.checked)}
              />
              <span className="text-body font-body text-default-font">
                Never expires
              </span>
            </div>
          </div>
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
            <Select
              className="h-auto w-full flex-none"
              label="Repeat Every"
              placeholder="Select frequency"
              value={repeatFrequency}
              onValueChange={(value: string) => setRepeatFrequency(value)}
            >
              <Select.Item value="daily">Daily</Select.Item>
              <Select.Item value="start_of_month">Start of Month</Select.Item>
              <Select.Item value="end_of_month">End of Month</Select.Item>
              <Select.Item value="end_of_quarter">End of Quarter</Select.Item>
              <Select.Item value="end_of_year">End of Year</Select.Item>
              <Select.Item value="date_of_month">Date of Month</Select.Item>
              <Select.Item value="every_n_days">Every N Days</Select.Item>
            </Select>
            {repeatFrequency === "every_n_days" && (
              <TextField
                className="h-auto w-full flex-none"
                label="Number of Days"
                helpText=""
              >
                <TextField.Input
                  type="number"
                  placeholder="Enter number of days"
                  value={recurrenceInterval.toString()}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setRecurrenceInterval(
                      event.target.value ? parseInt(event.target.value) : ""
                    )
                  }
                  min="1"
                />
              </TextField>
            )}
            <TextField
              className="h-auto w-full flex-none"
              label="Ends On"
              helpText=""
              icon={<FeatherCalendar />}
            >
              <TextField.Input
                type="date"
                placeholder="Select end date"
                value={endsOn}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEndsOn(event.target.value)
                }
                disabled={neverExpires}
                min={startsOn || today}
              />
            </TextField>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
          <Button
            variant="neutral-tertiary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Creating..." : "Create Recurring"}
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default MakeRecurringModal;
